<template>
  <div>
    <v-row>
      <v-col cols="12" md="11">
        <v-card class="pa-3">
          <v-card-title>
            <h4>{{$t($route.meta.breadcrumb[2].text)}}</h4>
          </v-card-title>
          <v-card-subtitle>
            <p>{{$t('tests.formMessage')}}</p>
          </v-card-subtitle>
          <v-card-text>
            <div v-if="loading">
              <v-progress-linear indeterminate :active="loading" color="primary"></v-progress-linear>
              <p class="text-center">{{$t('general.loading')}}</p>
            </div>
            <v-form
              lazy-validation
              v-if="show"
              ref="form"
              v-model="valid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    dense
                    :rules="rules.textRules"
                    v-model="test.name"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.name')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                 <v-col cols="12" md="6">
                  <v-autocomplete
                    outlined
                    dense
                    :items="countries"
                    item-text="name"
                    item-value="id"
                    :rules="rules.required"
                    v-model="test.country"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.country')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    outlined
                    dense
                    :items="siteCapitalizeWords"
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    small-chips
                    :error-messages="!this.new && test.sites.length==0 ? $t('validation.required') : ''"
                    :rules="rules.required"
                    v-model="test.sites"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.sites')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                    <template v-slot:selection="data">
                      <v-chip
                        small
                        color="primary">
                        {{data.item.name}}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-avatar>
                        <v-icon>fas fa-map-marked-alt</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="font-weight-black">{{data.item.name}}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          ( {{data.item.address.street + ' #' + data.item.address.street_number + `, ${$t('address.zip')}: ` + data.item.address.zip_code}} )
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                 <v-col cols="12" md="6">
                  <v-autocomplete
                    outlined
                    dense
                    :items="unitTypes"
                    item-text="alias"
                    item-value="id"
                    multiple
                    chips
                    small-chips
                    :rules="rules.required"
                    :error-messages="!this.new && test.unitTypes.length==0 ? $t('validation.required') : ''"
                    v-model="test.unitTypes"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.unitTypes')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                    <template v-slot:selection="data">
                      <v-chip
                        small
                        color="primary">
                        {{data.item.alias}}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <h3 class="black--text">{{$t('tests.sections')}}
                <v-chip small color="primary">
                  {{sections.length}}
                </v-chip>
              </h3> 
              <v-divider class="mt-2"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-expansion-panels>
                    <draggable tag="div" class="drag-container" :list="sections" handle=".handle">
                      <v-expansion-panel v-for="(section,idx) in sections" :key="idx">
                        <v-expansion-panel-header>
                          <p class="section-title">
                            <i class="fas fa-grip-vertical handle mr-2"></i> {{section.name}}
                          </p>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field v-model="section.name" outlined dense disabled>
                                <template v-slot:label>
                                  <span>{{$t('general.name')}}</span><span class="error--text">&nbsp;*</span>
                                </template>
                              </v-text-field>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <v-textarea
                                outlined
                                dense
                                disabled
                                v-model="section.description"
                              >
                                <template v-slot:label>
                                  <span>{{$t('general.description')}}</span><span class="error--text">&nbsp;*</span>
                                </template>
                              </v-textarea>
                            </v-col>
                          </v-row>
                          <h3>{{$t('tests.questions')}}
                            <v-chip small color="primary">
                              {{section.questions.length}}
                            </v-chip>
                          </h3>
                          <v-row>
                            <v-col cols="12">
                              <v-expansion-panels>
                                <draggable tag="div" class="drag-container" :list="section.questions" handle=".handle">
                                  <v-expansion-panel v-for="(question, id) in section.questions" :key="id">
                                    <v-expansion-panel-header expand-icon>
                                      <p class="question-title">
                                        <i class="fas fa-grip-vertical handle mr-2"></i> {{question.name}}
                                      </p>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                      <v-row>
                                        <v-col cols="12">
                                          <v-text-field v-model="question.name" outlined dense disabled>
                                            <template v-slot:label>
                                              <span>{{$t('general.name')}}</span><span class="error--text">&nbsp;*</span>
                                            </template>
                                          </v-text-field>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <v-col cols="12">
                                          <v-textarea v-model="question.description" outlined dense disabled>
                                            <template v-slot:label>
                                              <span>{{$t('general.description')}}</span><span class="error--text">&nbsp;*</span>
                                            </template>
                                          </v-textarea>
                                        </v-col>
                                      </v-row>
                                      <v-row>
                                        <v-col cols="12">
                                          <p class="float-right">
                                            <v-tooltip bottom>
                                              <template v-slot:activator="{ on }">
                                                <v-icon @click="changeComment(idx, id)" :color="getRequiredComment(question.config) ? 'primary' : 'secondary'" small class="mr-2 ml-2" v-on="on">far fa-comment</v-icon>
                                              </template>
                                              <span>{{ getRequiredComment(question.config) ? $t('tests.required') : $t('tests.noRequired') }}</span>
                                            </v-tooltip>
                                            <v-tooltip bottom>
                                              <template v-slot:activator="{ on }">
                                                <v-icon @click="changeCritic(idx, id)" :color="getIsCritical(question) ? 'warning' : 'secondary'" small class="mr-2" v-on="on">fas fa-exclamation-triangle</v-icon>
                                              </template>
                                              <span>{{ getIsCritical(question) ? $t('tests.critic') : $t('tests.noCritic') }}</span>
                                            </v-tooltip>
                                            <v-tooltip bottom>
                                              <template v-slot:activator="{ on }">
                                                <v-icon @click="removeQuestion.config[0](idx, id)" :color="getRequiredPhoto(question.config) ? 'success' : 'secondary'" small v-on="on">fas fa-camera</v-icon>
                                              </template>
                                              <span>{{ getRequiredPhoto(question.config) ? $t('tests.photoRequired') : $t('tests.photoNoRequired') }}</span>
                                            </v-tooltip>
                                          </p>
                                        </v-col>
                                      </v-row>
                                    </v-expansion-panel-content>
                                  </v-expansion-panel>
                                </draggable>
                              </v-expansion-panels>
                              <!-- <v-btn
                                class="mt-4"
                                block
                                tile
                                @click="addQuestion(idx)">
                                <v-icon
                                  class="mr-2">
                                  fas fa-plus-circle
                                </v-icon>
                                {{$t('tests.addQuestion')}}
                              </v-btn> -->
                            </v-col>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </draggable>
                  </v-expansion-panels>
                  <!-- <v-btn
                    class="mt-4"
                    block
                    tile
                    @click="addSection">
                    <v-icon
                      class="mr-2">
                      fas fa-plus-circle
                    </v-icon>
                    {{$t('tests.addSection')}}
                  </v-btn> -->
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-btn
                    class="float-right"
                    @click="save"
                    color="primary">
                    {{$t('general.save')}}
                  </v-btn>
                   <v-btn
                    outlined
                    class="float-right mr-3"
                    @click="redirect"
                    color="primary">
                    {{$t('general.cancel')}}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="deleteDialog"
      width="500"
    >
      <v-card>
        <v-card-title
          class="error--text grey lighten-5"
        >
          {{$t('tests.delSection')}}
        </v-card-title>

        <v-card-text class="pt-5">
          <span>{{$t('tests.delSectionMessage')}}</span>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="deleteDialog = false"
          >
           {{$t('general.cancel')}}
          </v-btn>
          <v-btn
            color="error"
            @click="removeSection"
          >
           {{$t('general.delete')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="300">
      <v-card>
        <v-card-title class="error white--text headline">{{$t('general.warning')}}</v-card-title>
        <v-card-text class="mt-5">
          {{message}} 
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" outlined @click="dialog = false">{{$t('general.accept')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from "vuedraggable"
export default {
  components: {
    draggable
  },
  data () {
    return {
      deleteDialog: false,
      valid: true,
      show: false,
      new: false,
      loading: false,
      dialog: false,
      flag: false,
      id: 0,
      idEdit: null,
      message: '',
      sections: [
        {
          description: '',
          name: '',
          position: 0,
          questions: [
            {
              name: '',
              description: '',
              comment_required: false,
              is_critical: false,
              position: 1
            }
          ]
        }
      ],
      test: {
        name: '',
        country: 150,
        sites: '',
        unitTypes: ''
      },
      countries: [],
      sites: [],
      unitTypes: []
    }
  },
  created () {
    if(this.$route.path === '/checklists/add') {
      this.show = true
      this.new = true
    } else {
      this.show = false
      this.loading = true
      this.idEdit = this.$route.params.id
      this.get()
    }
    this.$store.dispatch('getCountry', {
      event: {
        context: this
      }
    })
    this.$store.dispatch('sites/get', {
      event: {
        context: this,
        item: {
          perPage:-1,
          page: 1
        }
      }
    })
    this.$store.dispatch('unitTypes/getAll', {
      event: {
        context: this
      }
    })
  },
  computed: {
    rules () {
      return this.$validationRules()
    },
    siteCapitalizeWords() {
      if (this.sites.length === 0) return []

      return this.sites.map(site => {
        return {
          ...site,
          name: this.capitalizeWords(site.name)
        }
      })
    }
  },
  methods: {
    getRequiredComment(config) {
      return config && config.length > 0
        ? config.some(f => f.question_function_id === 3 && f.is_active === 1)
        : false
    },
    getRequiredPhoto(config) {
      return config && config.length > 0
        ? config.some(f => f.question_function_id === 2 && f.is_active === 1)
        : false
    },
    getIsCritical(question) {
      return question
        ? (question.blocking === 1)
        : false
    },
    get () {
      this.$store.dispatch('tests/getById', {
        event: {
          context: this,
          id: this.idEdit
        }
      })
    },
    redirect () {
      if (!this.new && this.emptySectionQuestions()) {
        this.dialog = true
        this.message = this.$t('tests.testValidation')
      } else {
        this.$router.push('/checklists')
      }
    },
    removeAt (idx) {
      this.id = idx
      this.deleteDialog = true
    },
    removeSection () {
      this.deleteDialog = false
      if (this.sections[this.id].id) {
        this.$store.dispatch('tests/deleteSection', {
          event: {
            context: this,
            sectionId: this.sections[this.id].id,
            testId: this.sections[this.id].questionnaire_id
          }
        })
      } else {
        this.sections.splice(this.id, 1)
      }
    },
    removeQuestion (idx, id) {
      if (this.sections[idx].questions[id].id) {
        this.$store.dispatch('tests/deleteQuestion', {
          event: {
            context: this,
            sectionId: this.sections[idx].id,
            questionId: this.sections[idx].questions[id].id
          }
        })
      } else {
        this.sections[idx].questions.splice(id, 1)
      }
    },
    addSection () {
      this.sections.push(
        {
          name: '',
          description: '',
          position: 0,
          questions: [
            {
              name: '',
              description: '',
              comment_required: false,
              is_critical: false,
              position: 0
            }
          ]
        }
      )
    },
    addQuestion (idx) {
      this.sections[idx].questions.push(
        {
          name: '',
          description: '',
          comment_required: false,
          is_critical: false,
          position: 0
        }
      )
    },
    changeComment (idx, id) {
      this.sections[idx].questions[id].comment_required = !this.sections[idx].questions[id].comment_required
    },
    changeCritic (idx, id) {
      this.sections[idx].questions[id].is_critical = !this.sections[idx].questions[id].is_critical
    },
    save () {
      // if (this.test.unitTypes.length !== 0 && this.test.sites.length !== 0) {
        if (this.$refs.form.validate()) {
          if (this.emptySectionQuestions()) {
            this.dialog = true
            this.message = this.$t('tests.testValidation')
          } else {
            if (this.checkSectionsAndQuestions()) {
              this.dialog = true
              this.message = this.$t('tests.noAnswered')
            } else {
              if (this.new) {
                this.order()
                this.$store.dispatch('tests/post', {
                  event: {
                    context: this,
                    test: {
                      name: this.test.name,
                      country_id: this.test.country,
                      unit_types: this.test.unitTypes,
                      sites: this.test.sites,
                      sections: this.sections
                    }
                  }
                })
              } else {
                this.order()
                this.$store.dispatch('tests/put', {
                  event: {
                    context: this,
                    test: {
                      name: this.test.name,
                      country_id: this.test.country,
                      unit_types: this.test.unitTypes,
                      sections: this.sections,
                      sites: this.test.sites
                    },
                    id: this.idEdit
                  }
                })
              }
            }
          }
        } else {
          if (this.emptySectionQuestions()) {
            this.dialog = true
            this.message = this.$t('tests.testValidation')
          } else {
            if (this.checkSectionsAndQuestions()) {
              this.dialog = true
              this.message = this.$t('tests.noAnswered')
            }
          }
        }
      // }
    },
    order () {
      this.sections.forEach((section, idx) => {
        section.position = idx
        section.questions.forEach((question, id) => {
          question.position = id
        })
      })
    },
    emptySectionQuestions () {
      let flag = false
      if (this.sections.length === 0) {
        flag = true
      } else {
        this.sections.forEach(section => {
          if (section.questions.length === 0) {
            flag = true
          }
        })
      }
      return flag
    },
    checkSectionsAndQuestions () {
      this.flag = false
      this.sections.forEach(section => {
        if (section.name === '' || section.description === '' ) {
          this.flag = true
        }
        section.questions.forEach(question => {
          if (question.name === '' || question.description === '' ) {
            this.flag = true
          }
        })
      })
      return this.flag
    },
    capitalizeWords(str) {
      if (!str) return ''
      
      return str
        .toLowerCase()
        .replace(/(^|\s)\p{L}/gu, c => c.toUpperCase());
    }
  }
}
</script>

<style scoped>
  .drag-container {
    width: 100%;
  }
  .fa-grip-vertical:hover {
    cursor: move;
  }
  .section-title {
    width: 95% !important;
  }
   .question-title {
     width: 92%;
   }
  .primary {
    color: #0091FF;
  }
</style>